/**
 * Public expo landing page (Feb 2026 R38). Renders a marketing-style
 * page for a specific expo consuming the /public/organizer/expos/{id}/landing
 * aggregate endpoint. Route: /public/expo/:expoId
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Calendar, MapPin, Ticket, Users, Building2, Presentation } from "lucide-react";

export default function PublicExpoLanding() {
  const { expoId } = useParams();
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/public/organizer/expos/${expoId}/landing`)
      .then((r) => setData(r.data))
      .catch(() => setData({ error: true }));
  }, [expoId]);

  if (!data) return <div className="p-12 text-center text-muted-foreground">Loading…</div>;
  if (data.error) return <div className="p-12 text-center text-muted-foreground">Expo not found.</div>;

  const { expo, tiers, sessions, exhibitors, stats, workspace } = data;
  const sessionsByTrack = sessions.reduce((acc, s) => {
    (acc[s.track_name] = acc[s.track_name] || []).push(s); return acc;
  }, {});

  return (
    <div className="min-h-screen bg-background" data-testid="public-expo-landing">
      {/* Hero */}
      <div className="border-b border-border bg-muted/30">
        <div className="max-w-5xl mx-auto px-6 py-16">
          <div className="flex items-center gap-6 mb-4">
            {workspace?.logo_url && (
              <img src={workspace.logo_url} alt="" className="w-16 h-16 border border-border object-contain" />
            )}
            <div className="text-[11px] uppercase tracking-widest text-primary font-semibold">Expo</div>
          </div>
          <h1 className="text-5xl font-black leading-tight mb-2">{expo.name}</h1>
          {expo.tagline && <div className="text-lg text-muted-foreground mb-6">{expo.tagline}</div>}
          <div className="flex flex-wrap gap-6 text-sm text-muted-foreground mb-8">
            <span className="inline-flex items-center gap-1.5"><Calendar className="w-4 h-4" /> {expo.start_date} → {expo.end_date}</span>
            {expo.venue_name && <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4" /> {expo.venue_name}{expo.city ? `, ${expo.city}` : ""}</span>}
          </div>
          {expo.description && <p className="text-base leading-relaxed max-w-2xl">{expo.description}</p>}
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Link
              to={`/public/expo/${expoId}/tickets`}
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 hover:opacity-90 font-semibold"
              data-testid="landing-cta-buy"
            >
              <Ticket className="w-4 h-4" /> Buy tickets
            </Link>
            <span className="text-xs text-muted-foreground">
              {tiers.length} tier{tiers.length === 1 ? "" : "s"} · {stats.sessions} session{stats.sessions === 1 ? "" : "s"} · {stats.exhibitors} exhibitor{stats.exhibitors === 1 ? "" : "s"}
            </span>
          </div>
        </div>
      </div>

      {/* Stats strip */}
      <div className="border-b border-border">
        <div className="max-w-5xl mx-auto px-6 py-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard icon={Ticket} label="Ticket tiers" value={tiers.length} />
          <StatCard icon={Presentation} label="Sessions" value={stats.sessions} />
          <StatCard icon={Users} label="Exhibitors" value={stats.exhibitors} />
          <StatCard icon={Building2} label="Booths sold" value={`${stats.booths_sold}/${stats.booths_total}`} />
        </div>
      </div>

      {/* Tiers */}
      {tiers.length > 0 && (
        <Section title="Tickets">
          <div className="grid md:grid-cols-3 gap-4">
            {tiers.map((t) => (
              <Link key={t.id} to={`/public/expo/${expoId}/tickets`} className="border border-border p-4 hover:border-primary transition-colors" data-testid={`landing-tier-${t.id}`}>
                <div className="font-bold text-lg">{t.name}</div>
                {t.description && <div className="text-xs text-muted-foreground mt-1">{t.description}</div>}
                <div className="mt-3 text-2xl font-mono font-black">{t.currency} {Number(t.price_amount).toLocaleString()}</div>
                {t.remaining !== null && <div className="text-[10px] text-muted-foreground mt-1">{t.remaining} left</div>}
              </Link>
            ))}
          </div>
        </Section>
      )}

      {/* Agenda */}
      {sessions.length > 0 && (
        <Section title="Agenda">
          {Object.keys(sessionsByTrack).sort().map((track) => (
            <div key={track} className="mb-6" data-testid={`landing-track-${track}`}>
              <div className="text-[11px] uppercase tracking-widest text-primary font-semibold mb-2">Track · {track}</div>
              <div className="space-y-2">
                {sessionsByTrack[track].map((s) => (
                  <div key={s.id} className="border border-border p-3 flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="font-semibold">{s.title}</div>
                      {s.speaker_name && <div className="text-xs text-muted-foreground">🎤 {s.speaker_name}</div>}
                      {s.description && <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{s.description}</div>}
                    </div>
                    <div className="text-right text-[11px] font-mono text-muted-foreground shrink-0">
                      {(s.start_at || "").slice(0, 16).replace("T", " ")}<br />
                      → {(s.end_at || "").slice(11, 16)}<br />
                      {s.room_name && <span className="text-[10px]">{s.room_name}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </Section>
      )}

      {/* Exhibitors */}
      {exhibitors.length > 0 && (
        <Section title="Exhibitors">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {exhibitors.map((ex, i) => (
              <div key={i} className="border border-border px-3 py-2 text-sm" data-testid={`landing-exhibitor-${i}`}>
                <div className="font-semibold truncate">{ex.name}</div>
                <div className="text-[10px] text-muted-foreground font-mono">Booth {ex.booth_code}</div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Footer CTA */}
      <div className="border-t border-border bg-muted/30">
        <div className="max-w-5xl mx-auto px-6 py-12 text-center">
          <div className="text-lg font-semibold mb-3">Ready to attend?</div>
          <Link
            to={`/public/expo/${expoId}/tickets`}
            className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 hover:opacity-90 font-semibold"
          >
            <Ticket className="w-4 h-4" /> Get your ticket
          </Link>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="border border-border p-4">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="text-2xl font-black mt-1">{value}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="max-w-5xl mx-auto px-6 py-10">
      <h2 className="text-2xl font-bold mb-6">{title}</h2>
      {children}
    </div>
  );
}
